import React, { useCallback, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import CircularProgress from '@mui/material/CircularProgress';

import { QUERY_WORKFLOWS_BY_SIMULATIONS } from 'client/app/api/gql/queries';
import * as SimulationsApi from 'client/app/api/SimulationsApi';
import { experimentsStyles } from 'client/app/apps/experiments/commonExperimentsStyles';
import {
  MessageType,
  NoEntitiesMessage,
} from 'client/app/apps/experiments/NoEntitiesMessage';
import RenameSimulationDialog from 'client/app/apps/experiments/Workflows/RenameSimulationDialog';
import { WorkflowCard } from 'client/app/components/cards/WorkflowCard';
import WorkflowCardExpandable, {
  WorklfowCardExpandableSkeletonList,
} from 'client/app/components/cards/WorkflowCardExpandable';
import {
  WorkflowsBySimulationsQuery,
  WorkflowsBySimulationsQueryVariables,
} from 'client/app/gql';
import { ContentType, FavoritedByFilterEnum, WorkflowSourceEnum } from 'client/app/gql';
import usePagination from 'client/app/hooks/usePagination';
import { useSimulationClient } from 'client/app/lib/SimulationClient';
import { ScreenRegistry } from 'client/app/registry';
import { PageInfo } from 'common/server/graphql/pagination';
import ConfirmationDialog from 'common/ui/components/Dialog/ConfirmationDialog';
import { EntityCardSkeletonList } from 'common/ui/components/EntityCard';
import { DateRange } from 'common/ui/components/FilterChip/FilterChipWithDateRange';
import { RenderQuery } from 'common/ui/components/RenderQuery/RenderQuery';
import { useSnackbarManager } from 'common/ui/components/SnackbarManager';
import { logEvent } from 'common/ui/GoogleAnalyticsUtils';
import makeStylesHook from 'common/ui/hooks/makeStylesHook';
import useDialog from 'common/ui/hooks/useDialog';

type WorkflowListProps = {
  scrollableRef: React.RefObject<HTMLDivElement>;
  searchQuery?: string;
  favorited: boolean;
  filterDateRange: DateRange;
  contentSource?: ContentType;
  filterUserId?: string;
  filterWorkflowTypes: WorkflowSourceEnum[];
  filterSuccessfulSimulations: boolean;
  dialog?: boolean;
  onClick?: (workflowId: WorkflowId) => void;
  disabledWorkflowIds?: WorkflowId[];
};

export function WorkflowList(props: WorkflowListProps) {
  const {
    dialog,
    scrollableRef,
    searchQuery,
    favorited,
    filterDateRange,
    contentSource,
    filterUserId,
    filterWorkflowTypes,
    filterSuccessfulSimulations,
    onClick,
    disabledWorkflowIds,
  } = props;

  const workflowQueryVariables = useMemo(
    () => ({
      search: searchQuery,
      favorited: favorited
        ? FavoritedByFilterEnum.FAVORITED_BY_ANYONE
        : FavoritedByFilterEnum.ALL,
      filterStartDate: filterDateRange.startDate
        ? filterDateRange.startDate.format('YYYY-MM-DD')
        : undefined,
      filterEndDate: filterDateRange.endDate
        ? filterDateRange.endDate.format('YYYY-MM-DD')
        : undefined,
      userId: filterUserId,
      workflowTypeFilter: filterWorkflowTypes ?? undefined,
      filterSimulationStatus: filterSuccessfulSimulations ? 'COMPLETED' : undefined,
      contentSource: contentSource,
    }),
    [
      contentSource,
      favorited,
      filterDateRange.endDate,
      filterDateRange.startDate,
      filterSuccessfulSimulations,
      filterUserId,
      filterWorkflowTypes,
      searchQuery,
    ],
  );

  const workflowsBySimulationQuery = useQuery(QUERY_WORKFLOWS_BY_SIMULATIONS, {
    variables: workflowQueryVariables,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const pageInfo = workflowsBySimulationQuery.data?.workflowsBySimulations.pageInfo as
    | PageInfo
    | undefined;
  const dependencies = [
    searchQuery,
    favorited,
    filterDateRange,
    filterUserId,
    filterSuccessfulSimulations,
  ];
  const hasNextPage = usePagination({
    entity: 'workflowsBySimulations',
    pageInfo,
    fetchMore: workflowsBySimulationQuery.fetchMore,
    dependencies,
    scrollableRef,
    isInitialLoading: workflowsBySimulationQuery.loading,
    variables: workflowQueryVariables,
  });

  return (
    <RenderQuery
      query={workflowsBySimulationQuery}
      renderData={WorkflowsListDataComponent}
      renderNoData={WorkflowsListNoDataComponent}
      loadingComponent={
        dialog ? EntityCardSkeletonList : WorklfowCardExpandableSkeletonList
      }
      additionalDataProps={{
        hasNextPage,
        disabledWorkflowIds,
        dialog,
        onClick,
        workflowQueryVariables,
      }}
      additionalNoDataProps={{
        searchQuery,
      }}
      emptyCondition={data => data.workflowsBySimulations.items.length === 0}
    />
  );
}

type WorkflowsListDataComponentProps = Pick<
  WorkflowListProps,
  'disabledWorkflowIds' | 'dialog' | 'onClick'
> & {
  data: WorkflowsBySimulationsQuery;
  hasNextPage: boolean;
  workflowQueryVariables: WorkflowsBySimulationsQueryVariables;
};

function WorkflowsListDataComponent(props: WorkflowsListDataComponentProps) {
  const {
    data,
    hasNextPage,
    disabledWorkflowIds,
    dialog,
    workflowQueryVariables,
    onClick,
  } = props;
  const classes = useStyles();

  const [confirmationDialog, openConfirmationDialog] = useDialog(ConfirmationDialog);
  const deleteSimulation = SimulationsApi.useDeleteSimulation();
  const snackbarManager = useSnackbarManager();

  const [renameSimulationDialog, openRenameSimulationDialog] =
    useDialog(RenameSimulationDialog);
  const simulationService = useSimulationClient();

  const onRenameSimulation = useCallback(
    async (
      simulationId: SimulationId,
      simulationName: string,
      workflowId: WorkflowId,
    ) => {
      const updatedSimulationName = await openRenameSimulationDialog({
        screenRegistry: ScreenRegistry.EXPERIMENTS,
        simulationName,
      });
      if (updatedSimulationName && updatedSimulationName !== simulationName) {
        logEvent('rename-simulation', ScreenRegistry.EXPERIMENTS, 'new-name');
        await simulationService.updateSimulation(
          simulationId,
          updatedSimulationName,
          workflowId,
          workflowQueryVariables,
        );
      } else {
        logEvent('rename-simulation', ScreenRegistry.EXPERIMENTS, 'no-change');
      }
    },
    [openRenameSimulationDialog, simulationService, workflowQueryVariables],
  );

  const onDeleteSimulation = useCallback(
    async (
      simulationId: SimulationId,
      simulationName: string,
      workflowId: WorkflowId,
    ) => {
      logEvent('delete-simulation', ScreenRegistry.EXPERIMENTS);
      const isDeleteConfirmed = await openConfirmationDialog({
        action: 'delete',
        isActionDestructive: true,
        object: 'simulation',
        specificObject: `${simulationName}`,
      });
      if (isDeleteConfirmed) {
        try {
          await deleteSimulation(simulationId, workflowId, workflowQueryVariables);
        } catch (e) {
          console.error(e);
          snackbarManager.showError(
            `Simulation:${simulationId} (${simulationName}) has been shared elsewhere and cannot be deleted. Please refresh the page.`,
          );
        }
      }
    },
    [deleteSimulation, openConfirmationDialog, snackbarManager, workflowQueryVariables],
  );

  return (
    <div className={classes.list}>
      {data.workflowsBySimulations.items.map(workflow =>
        dialog ? (
          <WorkflowCard
            key={workflow.id}
            workflow={workflow}
            onClick={() => onClick?.(workflow.id)}
            isDisabled={disabledWorkflowIds?.includes(workflow.id)}
          />
        ) : (
          <WorkflowCardExpandable
            key={workflow.id}
            workflow={workflow}
            onDeleteSimulation={onDeleteSimulation}
            onRenameSimulation={onRenameSimulation}
            workflowQueryVariables={workflowQueryVariables}
          />
        ),
      )}
      {hasNextPage && (
        <div className={classes.circularLoadingContainer}>
          <CircularProgress size={24} />
        </div>
      )}
      {confirmationDialog}
      {renameSimulationDialog}
    </div>
  );
}

type WorkflowsListNoDataComponentProps = { searchQuery?: string };

function WorkflowsListNoDataComponent(props: WorkflowsListNoDataComponentProps) {
  const { searchQuery } = props;
  return (
    <NoEntitiesMessage
      entityName="workflows"
      messageType={MessageType.NO_FILTER_RESULTS}
      searchQuery={searchQuery}
    />
  );
}

const useStyles = makeStylesHook(theme => ({
  ...experimentsStyles(theme),
}));
